import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Stack,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

import {
  GetDocumentById,
  UpdateDocument,
  DeleteDocument,
} from "../../hooks/useDocumentData";
import { documentGroups } from "../../constants/DocumentGroups";
import { metaForKey } from "../../constants/emergencyCategories";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";

const getExtension = (filename = "") => {
  const m = filename.match(/\.([a-z0-9]{1,5})$/i);
  return m ? m[1].toUpperCase() : "FIL";
};

const getDisplayName = (filename = "") => {
  if (!filename) return "Okänd fil";
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(0, dot) : filename;
};

const FileTile = ({ filename, categoryKey, size = 64 }) => {
  const meta = metaForKey(categoryKey);
  const ext = getExtension(filename);
  const extLength = ext.length;
  return (
    <Box
      sx={{
        width: size,
        height: size * 1.2,
        minWidth: size,
        borderRadius: "14px",
        background: `linear-gradient(155deg, ${meta.color} 0%, ${meta.dark} 100%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        position: "relative",
        overflow: "hidden",
        boxShadow: `0 1px 0 rgba(255,255,255,0.35) inset, 0 14px 28px -14px ${meta.color}88`,
        transition: "background 0.4s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: size * 0.26,
          height: size * 0.26,
          background: "rgba(255,255,255,0.18)",
          clipPath: "polygon(0 0, 100% 100%, 100% 0)",
        },
        "&::after": {
          content: '""',
          position: "absolute",
          inset: 0,
          borderRadius: "14px",
          background:
            "radial-gradient(circle at 20% 15%, rgba(255,255,255,0.22), transparent 55%)",
          pointerEvents: "none",
        },
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 700,
          fontSize: extLength > 3 ? size * 0.22 : size * 0.28,
          letterSpacing: "-0.01em",
          color: "#fff",
          mb: size * 0.18 + "px",
          position: "relative",
          zIndex: 1,
          textShadow: "0 1px 2px rgba(0,0,0,0.15)",
        }}
      >
        {ext}
      </Typography>
    </Box>
  );
};

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const CategoryOption = ({ groupKey, label, active, onClick }) => {
  const meta = metaForKey(groupKey);
  const Icon = meta.icon;
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 1.75,
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        background: active ? meta.tint : "#fff",
        border: `1px solid ${active ? meta.border : LINE}`,
        borderRadius: "12px",
        position: "relative",
        overflow: "hidden",
        transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
        fontFamily: '"DM Sans", sans-serif',
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: meta.color,
          transform: active ? "scaleY(1)" : "scaleY(0)",
          transformOrigin: "center",
          transition: "transform 0.35s cubic-bezier(0.22,1,0.36,1)",
        },
        "&:hover": {
          borderColor: active ? meta.border : "rgba(10,22,40,0.2)",
        },
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          minWidth: 40,
          borderRadius: "10px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: active
            ? `linear-gradient(135deg, ${meta.color}, ${meta.dark})`
            : "#f4f2ee",
          color: active ? "#fff" : INK_MUTED,
          transition: "all 0.35s ease",
          boxShadow: active ? `0 10px 22px -10px ${meta.color}80` : "none",
        }}
      >
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Typography
        sx={{
          flex: 1,
          fontFamily: '"Outfit", sans-serif',
          fontWeight: 600,
          fontSize: "15px",
          color: active ? meta.color : INK,
          letterSpacing: "-0.005em",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: "50%",
          border: `1.5px solid ${active ? meta.color : "rgba(10,22,40,0.18)"}`,
          background: active ? meta.color : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.25s ease",
          flexShrink: 0,
        }}
      >
        {active && <CheckRoundedIcon sx={{ fontSize: 13, color: "#fff" }} />}
      </Box>
    </Box>
  );
};

export const DocumentEditModal = ({ documentId, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [edited, setEdited] = useState(false);
  const [documentData, setDocumentData] = useState({});
  const [documentGroup, setDocumentGroup] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: document, isLoading } = GetDocumentById(documentId);

  useEffect(() => {
    const docData = document?.data || document;
    if (docData) {
      setDocumentData(docData);
      setDocumentGroup(docData.document_group || "");
    }
  }, [document]);

  useEffect(() => {
    if (!confirmDelete) return undefined;
    const t = setTimeout(() => setConfirmDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmDelete]);

  const updateDocumentMutation = UpdateDocument({
    onSuccess: () => {
      enqueueSnackbar("Dokument uppdaterat.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte uppdatera dokument.", { variant: "error" });
    },
  });

  const deleteDocumentMutation = DeleteDocument({
    onSuccess: () => {
      enqueueSnackbar("Dokument raderat.", { variant: "success" });
      onClose && onClose();
    },
    onError: () => {
      enqueueSnackbar("Kunde inte radera dokument.", { variant: "error" });
      setConfirmDelete(false);
    },
  });

  const handleUpdate = () => {
    updateDocumentMutation.mutate({
      documentId,
      documentData: { document_group: documentGroup },
    });
    setEdited(false);
  };

  const handleDeleteClick = () => {
    if (confirmDelete) {
      deleteDocumentMutation.mutate(documentId);
    } else {
      setConfirmDelete(true);
    }
  };

  const handleGroupChange = (key) => {
    setDocumentGroup(key);
    setEdited(true);
  };

  const filename = documentData?.filename || "";
  const saving = updateDocumentMutation.isLoading;
  const deleting = deleteDocumentMutation.isLoading;
  const canSave = edited && !saving && !!documentGroup;

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2.5,
          mb: 4,
          pb: 3,
          borderBottom: `1px solid ${LINE_SOFT}`,
        }}
      >
        <FileTile filename={filename} categoryKey={documentGroup} size={64} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontSize: "10.5px",
              fontWeight: 700,
              letterSpacing: "0.22em",
              textTransform: "uppercase",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
              mb: 0.5,
            }}
          >
            Dokument · {getExtension(filename)}
          </Typography>
          {isLoading && !filename ? (
            <Skeleton width="65%" height={30} />
          ) : (
            <Typography
              sx={{
                fontFamily: '"Outfit", sans-serif',
                fontSize: "1.5rem",
                fontWeight: 600,
                color: INK,
                letterSpacing: "-0.025em",
                lineHeight: 1.15,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                wordBreak: "break-all",
              }}
            >
              {getDisplayName(filename) || "Okänd fil"}
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Kategori</SectionLabel>
        <Stack spacing={1.25}>
          {documentGroups.map((g) => (
            <CategoryOption
              key={g.key}
              groupKey={g.key}
              label={g.label}
              active={documentGroup === g.key}
              onClick={() => handleGroupChange(g.key)}
            />
          ))}
        </Stack>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={handleDeleteClick}
          disabled={deleting}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            background: confirmDelete ? BRAND : "transparent",
            color: confirmDelete ? "#fff" : BRAND,
            border: `1px solid ${
              confirmDelete ? BRAND : "rgba(138,17,37,0.26)"
            }`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: deleting ? "wait" : "pointer",
            transition: "all 0.25s ease",
            opacity: deleting ? 0.6 : 1,
            "&:hover": {
              background: confirmDelete ? "#6b0d1d" : "rgba(138,17,37,0.06)",
              borderColor: BRAND,
            },
          }}
        >
          {deleting ? (
            <CircularProgress size={14} sx={{ color: BRAND }} />
          ) : (
            <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
          )}
          {confirmDelete ? "Klicka igen för att bekräfta" : "Ta bort dokument"}
        </Box>

        <Box sx={{ display: "flex", gap: 1.25 }}>
          <Box
            component="button"
            type="button"
            onClick={onClose}
            sx={{
              px: 2.25,
              py: 1.25,
              background: "transparent",
              color: INK_MUTED,
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.25s ease",
              "&:hover": {
                color: INK,
                borderColor: "rgba(10,22,40,0.22)",
              },
            }}
          >
            Avbryt
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleUpdate}
            disabled={!canSave}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.75,
              py: 1.25,
              background: INK,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: canSave ? "pointer" : "not-allowed",
              opacity: canSave ? 1 : 0.4,
              transition: "all 0.25s ease",
              boxShadow: canSave
                ? "0 10px 24px -12px rgba(10,22,40,0.55)"
                : "none",
              "&:hover": canSave
                ? {
                    background: "#1a2638",
                    transform: "translateY(-1px)",
                  }
                : {},
            }}
          >
            {saving ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <CheckRoundedIcon sx={{ fontSize: 16 }} />
            )}
            Spara ändringar
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
